import ActivityRow from '@/components/common/ActivityRow';
import { ActivityRowParams } from '@/components/common/ActivitySection';
import Icon from '@/components/common/icons/Icon';
import { InfiniteList } from '@/components/common/InfiniteList';
import { hoverStyleProps } from '@/components/common/styleHelpers';
import { Highlight, Text } from '@/components/common/Typography';
import { showGoalTransactionStatusModal } from '@/components/goals/GoalTransactionStatusModal';
import { toDollars } from '@/util/currency';
import { useQuery } from '@apollo/client';
import { FlexProps } from '@chakra-ui/react';
import { Goal } from '@common/goals';
import { GoalActivitiesDocument, PartnerInfo } from '@common/graphql/generated';
import { getAmountRecurrenceStr } from '@common/react/goals';
import { DepositOrWithdrawal } from '@common/react/transactions';
import { getPartnerName, useCurrentUser } from '@common/react/user';
import { ActivityTypeInfo } from '@common/types';
import { dt } from '@common/util/dateTime';
import { FC } from 'react';
import { EntityViewGridSection } from '../common/EntityView';
import IconBackground from '../common/icons/IconBackground';

export interface GoalActivitiesSectionProps extends FlexProps {
    goal: Goal;
    filterByGoalAccountId?: string;
    showTitle?: boolean;
}

export const ITEMS_PER_PAGE = 5;

const GoalActivitiesSection: FC<GoalActivitiesSectionProps> = ({
    goal,
    filterByGoalAccountId,
    showTitle = true,
    ...props
}) => {
    const { partnerInfo } = useCurrentUser();
    const result = useQuery(GoalActivitiesDocument, {
        variables: {
            goalId: goal.id,
            financialEntityFilter: filterByGoalAccountId
                ? [
                      { financialEntityId: { equalTo: filterByGoalAccountId } },
                      { financialEntityId: { isNull: true } },
                  ]
                : [{ financialEntityId: { isNull: true } }],
            itemsPerPage: ITEMS_PER_PAGE,
        },
        fetchPolicy: 'network-only',
    });
    if (!result.data?.activitiesConnection) return null;
    const {
        edges: activities,
        pageInfo: { hasNextPage, endCursor },
    } = result.data.activitiesConnection || {};

    return (
        <EntityViewGridSection
            title={showTitle && 'Recent activities'}
            headerProps={{ mb: 5 }}
            {...props}
        >
            <InfiniteList
                items={activities}
                itemsPerPage={ITEMS_PER_PAGE}
                renderItem={(item) => {
                    const { id, typeInfo, dateTime } = item.node;
                    const activityRowParams = getGoalActivityRowParams({
                        goal,
                        typeInfo,
                        dateTime,
                        partnerInfo,
                    });
                    if (!activityRowParams) return null;

                    const { iconName, iconProps, ...activityRowProps } =
                        activityRowParams;

                    return (
                        <ActivityRow
                            key={id}
                            dateTime={dateTime}
                            {...activityRowProps}
                            {...(activityRowProps.onClick
                                ? hoverStyleProps({
                                      bg: 'gray.100',
                                  })
                                : {})}
                            px={2}
                            ml={-1}
                            icon={
                                <IconBackground>
                                    <Icon
                                        color="gray.600"
                                        size="sm"
                                        {...iconProps}
                                        name={iconName}
                                    />
                                </IconBackground>
                            }
                        />
                    );
                }}
                emptyState="No activities, yet"
                queryResult={result}
                endCursor={endCursor}
                hasNextPage={hasNextPage}
            />
        </EntityViewGridSection>
    );
};

export const getTransactionActivityRowParams = ({
    transactionDirection,
    typeInfo,
    onClick,
}: {
    transactionDirection: DepositOrWithdrawal;
    typeInfo: ActivityTypeInfo;
    onClick?: () => void;
}): ActivityRowParams => {
    if (
        typeInfo.type !== 'FE_WITHDRAWAL_CREATED' &&
        typeInfo.type !== 'FE_DEPOSIT_CREATED'
    ) {
        return { leftText: '', iconName: 'Plus' };
    }

    const plannedTransactionDate =
        typeInfo.plannedProcessingDate &&
        dt(typeInfo.plannedProcessingDate).isAfter(dt(), 'day') &&
        dt(typeInfo.plannedProcessingDate).format('MMM D');

    const isPending = typeInfo.transactionStatus === 'PENDING';
    const isDeposit = transactionDirection === 'DEPOSIT';
    const transactionContent = isDeposit ? 'Deposit' : 'Withdrawal';

    const amount = isDeposit ? typeInfo.amount : -typeInfo.amount;

    if (typeInfo.transactionStatus === 'FAILED') {
        return {
            leftText: `${transactionContent} failed (${toDollars(amount, {
                withCents: true,
                alwaysSigned: true,
            })})`,
            iconName: 'AlertCircle',
            onClick,
        };
    }

    return {
        rightText: isPending ? (
            plannedTransactionDate ? (
                <Text
                    color="gray.400"
                    fontSize="sm"
                >{`on ${plannedTransactionDate}`}</Text>
            ) : (
                <Highlight colorScheme="green">in progress</Highlight>
            )
        ) : undefined,
        leftText: `${transactionContent} (${toDollars(amount, {
            withCents: true,
            alwaysSigned: true,
        })})`,
        leftTextColor: isPending ? 'gray.400' : undefined,
        iconName: isDeposit ? 'Plus' : 'Minus',
        onClick,
    };
};

export const getGoalActivityRowParams = ({
    goal,
    typeInfo,
    dateTime,
    partnerInfo,
}: {
    goal: Goal;
    typeInfo: ActivityTypeInfo;
    dateTime: string;
    partnerInfo: PartnerInfo | null | undefined;
}): ActivityRowParams | null => {
    const partnerName = getPartnerName(partnerInfo);

    switch (typeInfo.type) {
        case 'DIVIDEND_REALIZED':
            return {
                leftText: `${dt(dateTime).format(
                    'MMM YYYY'
                )} Dividends (${toDollars(typeInfo.amount, {
                    withCents: true,
                    alwaysSigned: true,
                })})`,
                iconName: 'Plus',
            };

        case 'FE_DEPOSIT_CREATED':
            return getTransactionActivityRowParams({
                transactionDirection: 'DEPOSIT',
                typeInfo,
                onClick: () => {
                    showGoalTransactionStatusModal({
                        transactionId: typeInfo.transactionId,
                        transactionDirection: 'DEPOSIT',
                    });
                },
            });

        case 'FE_WITHDRAWAL_CREATED':
            return getTransactionActivityRowParams({
                transactionDirection: 'WITHDRAWAL',
                typeInfo,
                onClick: () => {
                    showGoalTransactionStatusModal({
                        transactionId: typeInfo.transactionId,
                        transactionDirection: 'WITHDRAWAL',
                    });
                },
            });

        case 'FEE_REALIZED':
            return {
                leftText: `${dt(dateTime).format('MMM YYYY')} Fees (${toDollars(
                    typeInfo.amount,
                    {
                        withCents: true,
                        alwaysSigned: true,
                    }
                )})`,
                iconName: 'Minus',
            };

        case 'GOAL_CONTRIBUTING_PAUSED': {
            return {
                leftText: `Paused contributing`,
                iconName: 'Pause',
            };
        }
        case 'GOAL_CONTRIBUTING_RESUMED': {
            return {
                leftText: `Resumed contributing`,
                iconName: 'Play',
            };
        }
        case 'GOAL_CONTRIBUTION_SCHEDULE_SET': {
            return {
                leftText: `Set contribution (${getAmountRecurrenceStr({
                    amount: typeInfo.amount,
                    recurrence: typeInfo.recurrence,
                })})`,
                iconName: 'CalenderCheck01',
            };
        }
        case 'GOAL_CONTRIBUTION_SCHEDULE_CHANGED': {
            return {
                leftText: `Changed contribution (${getAmountRecurrenceStr({
                    amount: typeInfo.amount,
                    recurrence: typeInfo.recurrence,
                })})`,
                iconName: 'CalenderCheck01',
            };
        }
        case 'GOAL_CREATED': {
            return {
                // TODO: Consider whether `name` should be included in `typeInfo` instead
                leftText: `${goal.name} goal created`,
                iconName: 'Star01',
            };
        }
        case 'GOAL_FUNDING_STARTED':
            return {
                leftText: `Started funding goal`,
                iconName: 'MarkerPin05',
            };

        case 'INTEREST_REALIZED':
            return {
                leftText: `${dt(dateTime).format(
                    'MMM YYYY'
                )} Interest (${toDollars(typeInfo.amount, {
                    withCents: true,
                    alwaysSigned: true,
                })})`,
                iconName: 'Plus',
            };

        case 'PLENTY_IRA_CREATED':
            return typeInfo.iraType
                ? {
                      leftText: {
                          ROTH: 'Opened Plenty Roth IRA',
                          TRADITIONAL: 'Opened Plenty Traditional IRA',
                      }[typeInfo.iraType],
                      iconName: 'Star01',
                  }
                : null;

        case 'TAX_REALIZED':
            return {
                leftText: `${dt(dateTime).format('MMM YYYY')} Foreign withholding tax (${toDollars(
                    typeInfo.amount,
                    {
                        withCents: true,
                        alwaysSigned: true,
                    }
                )})`,
                iconName: 'Minus',
            };

        case 'STOPPED_TRANSACTION_SCHEDULE': {
            return {
                leftText: `Stopped recurring transaction`,
                iconName: 'Stop',
            };
        }

        case 'PARTNER_LEFT_SPLIT_GOAL': {
            return {
                leftText: `${partnerName} removed themselves from this goal`,
                iconName: 'Lock01',
            };
        }

        // The following types should be ignored here:
        case 'FE_CREATED':
            return null;
        default: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const exhaustiveCheck: never = typeInfo;
            return null;
        }
    }
};

export default GoalActivitiesSection;
